import { useEffect, useRef, useState } from "react";
import { X, Loader2, Send } from "lucide-react";
import api from "../../../../shared/services/api";
import { enviarTemplate } from "../../services/mensagensApi";
import TemplatePreview from "../../../../shared/components/whatsapp/TemplatePreview";

/**
 * Modal para selecionar e enviar um template aprovado.
 *
 * @param {{
 *   contaId: number,
 *   conversaId: number,
 *   conversa?: object,        // dados da conversa pra chips de variável
 *   onClose: () => void,
 *   onSent: (mensagem: object) => void,
 * }} props
 */
export default function TemplateSelectorModal({ contaId, conversaId, conversa, onClose, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [variaveis, setVariaveis] = useState({});
  const [enviando,  setEnviando]  = useState(false);
  const [erro,      setErro]      = useState(null);
  const modalRef = useRef(null);

  // Chips de "valores rápidos" derivados da conversa atual. Click no chip
  // injeta o valor no input do placeholder em foco. Usuário ainda pode editar.
  const nomeCompleto = (conversa?.cliente_nome || "").trim();
  const primeiroNome = nomeCompleto.split(/\s+/)[0] || "";
  const telefone     = conversa?.wa_id || "";
  const dataHoje     = new Date().toLocaleDateString("pt-BR");
  const chips = [
    { label: "Nome",          value: nomeCompleto },
    { label: "Primeiro nome", value: primeiroNome },
    { label: "Telefone",      value: telefone     },
    { label: "Data hoje",     value: dataHoje     },
  ].filter((c) => c.value);

  useEffect(() => { modalRef.current?.focus(); }, []);

  useEffect(() => {
    function handleEsc(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    api
      .get(`/whatsapp/meta/contas/${contaId}/templates/aprovados`)
      .then((resp) => {
        if (cancelado) return;
        const lista = resp.data?.data ?? resp.data ?? [];
        setTemplates(Array.isArray(lista) ? lista : []);
      })
      .catch(() => { if (!cancelado) setTemplates([]); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [contaId]);

  function handleSelect(tmpl) {
    setSelected(tmpl);
    // Pré-preenche placeholder 1 com primeiro nome do contato (se houver) —
    // economiza um clique no caso comum "Olá, {{1}}!".
    setVariaveis(primeiroNome ? { "1": primeiroNome } : {});
    setErro(null);
  }

  function extrairPlaceholders(tmpl) {
    if (!tmpl) return [];
    const body = tmpl.components?.find((c) => c.type === "BODY");
    if (!body?.text) return [];
    const matches = body.text.match(/\{\{(\d+)\}\}/g) ?? [];
    return [...new Set(matches)].sort();
  }

  async function handleEnviar() {
    if (!selected) return;
    const placeholders   = extrairPlaceholders(selected);
    const componentParams = placeholders.map((ph) => {
      const n = ph.replace(/[{}]/g, "");
      return { type: "text", text: variaveis[n] ?? "" };
    });

    setEnviando(true);
    setErro(null);
    try {
      const resultado = await enviarTemplate(conversaId, {
        templateName: selected.name,
        languageCode: selected.language,
        components:   componentParams,
      });
      onSent(resultado);
      onClose();
    } catch (err) {
      setErro(err?.response?.data?.error ?? "Erro ao enviar template.");
    } finally {
      setEnviando(false);
    }
  }

  const placeholders = extrairPlaceholders(selected);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Selecionar template"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-surface-1 border border-white/[0.08] rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06] flex-shrink-0">
          <h2 className="text-sm font-semibold text-ink-1">Enviar template aprovado</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar modal"
            className="text-ink-4 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Lista de templates */}
          <div className="w-[44%] border-r border-white/[0.06] overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 flex-shrink-0">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-ink-4 text-center py-8 px-4">
                Nenhum template aprovado encontrado.
              </p>
            ) : (
              templates.map((tmpl) => (
                <button
                  key={tmpl.id}
                  type="button"
                  onClick={() => handleSelect(tmpl)}
                  aria-pressed={selected?.id === tmpl.id}
                  className={`w-full text-left px-4 py-3 border-b border-white/[0.06] transition-colors focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-brand-500 ${
                    selected?.id === tmpl.id
                      ? "bg-brand-900 border-l-2 border-l-purple-500"
                      : "hover:bg-surface-2"
                  }`}
                >
                  <p className="text-xs font-medium text-ink-1 truncate">{tmpl.name}</p>
                  <p className="text-xs text-ink-4 mt-0.5">{tmpl.language}</p>
                </button>
              ))
            )}
          </div>

          {/* Preview + variáveis */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700 px-4 py-4 flex flex-col gap-4">
            {!selected ? (
              <p className="text-xs text-ink-5 text-center mt-8">
                Selecione um template para visualizar
              </p>
            ) : (
              <>
                <div>
                  <p className="text-xs font-semibold text-ink-3 mb-2 uppercase tracking-wide">
                    Preview
                  </p>
                  <TemplatePreview template={selected} variaveis={variaveis} />
                </div>

                {placeholders.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-ink-3 mb-2 uppercase tracking-wide">
                      Variáveis
                    </p>
                    <div className="flex flex-col gap-3">
                      {placeholders.map((ph) => {
                        const n = ph.replace(/[{}]/g, "");
                        return (
                          <div key={ph} className="flex flex-col gap-1">
                            <label htmlFor={`var-${n}`} className="text-xs text-ink-3">
                              {ph}
                            </label>
                            {chips.length > 0 && (
                              <div className="flex flex-wrap gap-1" role="toolbar" aria-label={`Inserir valor em ${ph}`}>
                                {chips.map((chip) => (
                                  <button
                                    key={chip.label}
                                    type="button"
                                    onClick={() => setVariaveis((prev) => ({ ...prev, [n]: chip.value }))}
                                    title={`Inserir: ${chip.value}`}
                                    className="text-[10px] bg-surface-2 hover:bg-brand-900 hover:text-brand-300 text-ink-3 border border-white/[0.08] rounded px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
                                  >
                                    {chip.label}
                                  </button>
                                ))}
                              </div>
                            )}
                            <input
                              id={`var-${n}`}
                              type="text"
                              value={variaveis[n] ?? ""}
                              onChange={(e) =>
                                setVariaveis((prev) => ({ ...prev, [n]: e.target.value }))
                              }
                              placeholder={`Valor para ${ph}`}
                              className="bg-surface-2 border border-white/[0.08] rounded px-2.5 py-1.5 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {erro && (
                  <p className="text-xs text-red-400" role="alert">{erro}</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ink-3 hover:text-ink-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded px-3 py-1.5"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleEnviar}
            disabled={!selected || enviando}
            aria-label="Enviar template selecionado"
            className="flex items-center gap-1.5 text-xs font-medium bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg px-4 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            {enviando
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Send className="w-3.5 h-3.5" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
